import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, ArrowRight } from '@phosphor-icons/react';
import { fetchUnallocatedFunds, type UnallocatedFunds, type UnallocatedFundItem } from '@/lib/paymentsApi';
import { AllocatePaymentModal } from '@/components/payments/AllocatePaymentModal';

export function FundsToAllocate() {
  const qc = useQueryClient();
  const [active, setActive] = useState<UnallocatedFundItem | null>(null);

  const q = useQuery<UnallocatedFunds>({
    queryKey: ['unallocated-funds'],
    queryFn: fetchUnallocatedFunds,
    staleTime: 60_000,
  });

  const data = q.data;
  if (!data || data.count === 0) return null; // quiet when nothing is waiting to be allocated

  const money = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: data.currency, maximumFractionDigits: 0 }).format(n);
  const moneyExact = (n: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: data.currency, maximumFractionDigits: 2 }).format(n);
  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

  return (
    <>
      <section className="rounded-md border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold tracking-tight text-foreground">Funds to allocate</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {money(data.totalUnallocated)} received · {data.count} payment{data.count === 1 ? '' : 's'} · not yet matched to fee notes
            </p>
          </div>
          <Coins size={18} weight="duotone" className="text-muted-foreground" />
        </div>

        <ul className="divide-y divide-border">
          {data.items.map((item) => (
            <li key={item.paymentId}>
              <button
                type="button"
                onClick={() => setActive(item)}
                className="flex w-full items-center gap-3 px-5 py-2.5 text-left hover:bg-muted/40"
              >
                <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-xs font-medium text-foreground">{item.payer ?? 'Unattributed payment'}</span>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{moneyExact(item.unallocated)}</span>
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                    received {fmtDate(item.paymentDate)} · {moneyExact(item.allocated)} of {moneyExact(item.total)} allocated
                  </p>
                </div>
                <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-brand-600 dark:text-brand-400">
                  Allocate
                  <ArrowRight size={11} weight="bold" />
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {active && (
        <AllocatePaymentModal
          paymentId={active.paymentId}
          payer={active.payer}
          unallocated={active.unallocated}
          currency={active.currency}
          onClose={() => setActive(null)}
          onAllocated={() => {
            setActive(null);
            qc.invalidateQueries({ queryKey: ['unallocated-funds'] });
            qc.invalidateQueries({ queryKey: ['money-requiring-attention'] });
          }}
        />
      )}
    </>
  );
}
